import { makeAutoObservable, observable, runInAction } from 'mobx';
import type aiMediaAgent from '../../services/api/aiMediaAgent';
import { resolveErrorMessage } from '../../utils/resolveErrorMessage';
import { IN_DEBT_FOR_AN_IMAGE_MESSAGE, NOT_ENOUGH_CREDITS_FOR_AN_IMAGE_MESSAGE } from '../../utils/notEnoughCredits';
import type { TCreditsRefusalReason } from '../../utils/notEnoughCredits';
import type { IAiMediaConnection } from '../../services/api/creatorHubConnectionContract';
import {
  MAX_PROMPT_LENGTH,
  TGenerateImageOutcome,
  TGenerationAllowance,
  TMediaStyle,
  TPlaceholderContentIdentity,
  TSignalRMediaGenerationEnvelope,
  TSignalRMediaGenerationFinishedData,
} from '../../services/models/AiMediaModels';
import { EContentType, IContent, IPagePlaceholder } from '../../services/models/PageViewModel';
import type { TQuotaBalance } from '../../services/models/QuotaModels';
import { parseMediaGenerationStatusProjection, TMediaGenerationStatus } from './statusProjection';
import { rememberMediaStyleKey, readRememberedMediaStyleKey } from './styleMemory';

/**
 * What an image content's rendering branch shows. `image` is the ordinary image path — every
 * content that is not AI-marked gets it unconditionally, which is what keeps them untouched.
 *
 * The failed state is the backend's word and never this store's own: every path that gives up writes
 * `failed` onto the content and pushes a terminal event. It carries the backend's own words when it
 * has them, and null when it does not — a reason only ever arrives on a terminal event, so a page
 * reloaded after a failure has the state without the explanation.
 */
export type TMediaGenerationDisplayState = { kind: 'image' } | { kind: 'placeholder' } | { kind: 'generating'; secondsElapsed: number; isOverdue: boolean } | { kind: 'failed'; reason: string | null };

/**
 * When a wait stops being one this store can account for.
 *
 * Deliberately not a deadline of ours. The backend's deadline is ten minutes of *drawing*, while the
 * projection is stamped when the author asked — not when a worker took the job — so everything spent
 * waiting in the queue is counted here too. A burst deeper than the media lane's workers can clear
 * therefore carries a perfectly healthy generation past this mark, which is why passing it says only
 * that the wait can no longer be explained, never that the generation is over.
 *
 * What it is for is the one case the backend cannot cover: the projection write and the terminal
 * event are both best-effort, so a generation whose ending was lost twice leaves its content counting
 * with nothing left to correct it. Past this mark the slot says the picture may still be coming and
 * offers the way out, rather than inventing a failure that would invite the author to pay for a
 * second one.
 *
 * Set inside the backend's ten-minute drawing deadline on purpose: the wait an author will not sit
 * through is shorter than the wait the pipeline allows itself, so the slot speaks up first. It is one
 * mark and not two, so passing it also hands the author their buttons back.
 */
const OVERDUE_AFTER_MS = 8 * 60 * 1000;

/**
 * What the generation dialog stands over.
 *
 * A slot the page AI planned already has its content, and the dialog only edits it. One reached from
 * the media menu has a placeholder and nothing else: create-on-Generate means no content exists
 * until the author commits, which is what makes opening the dialog and closing it again free. A
 * submission that creates one flips the target to it, so a retry generates into the content already
 * on the page rather than putting a second one beside it.
 */
export type TGenerationDialogTarget = { kind: 'content'; content: IContent } | { kind: 'emptyPlaceholder'; placeholder: IPagePlaceholder };

/** Said when the page could not put an image content in the placeholder at all. */
const CONTENT_NOT_CREATED_MESSAGE = 'The image could not be added to the page. Please try again.';

/**
 * A backend refusal is prose written for the author — a blocked description to reword, a busy queue
 * that billed nothing — so it is shown verbatim and never parsed. Only a failure that says nothing
 * at all falls back to wording of our own.
 */
const GENERATION_REFUSED_MESSAGE = 'The image could not be generated. Please try again.';

/** Which course a dialog stands in, whichever of the two things it stands over. */
const courseIdOf = (target: TGenerationDialogTarget): number => (target.kind === 'content' ? target.content.courseID : target.placeholder.courseID);

/**
 * The single place that decides what an AI-marked image content shows, and the single place that
 * holds the generation dialog.
 *
 * The rendering branches are thin observers: they hand a page-load-shaped content in and render the
 * display state that comes back, deciding nothing themselves. All of it reads the **status
 * projection** on the content's AI internal data property — and reads it for rendering only. The
 * projection never decides whether a generation is truly in flight; the backend's generation table
 * is the source of truth for that, and this store never pretends otherwise.
 */

/**
 * Callbacks into the existing page and property stores, held as a port so those stores never gain a
 * seam of their own. Deliberately narrow: the store asks for the one property it writes and knows
 * nothing of the property tree it was dug out of.
 */
export interface IMediaGenerationPageCallbacks {
  /**
   * Persists the prompt onto the content's AI prompt property, the way the property panel writes
   * any other property. Resolves without writing when the content has no such property.
   */
  savePrompt: (content: IContent, prompt: string) => Promise<void>;
  /**
   * Persists the status projection onto the content's AI internal data property, the same way the
   * prompt is written. Resolves without writing when the content has no such property.
   *
   * The projection is the backend's to write on everything a generation does; the editor writes it
   * only where an author's own action ends the slot's AI lifecycle, which is a change no generation
   * is ever going to announce.
   */
  saveStatusProjection: (content: IContent, statusProjection: string) => Promise<void>;
  /**
   * Creates an empty image content in a placeholder that has none and puts it on the page. Null
   * when the page could not create one. Called at one moment only — the media-menu flow's Generate.
   */
  createImageContent: (placeholder: IPagePlaceholder) => Promise<IContent | null>;
  /**
   * Every content on the page the author has open, for a terminal event to be matched against.
   * The page, not the match: which of them an event names is decided here.
   */
  getLoadedContents: () => IContent[];
}

export interface IMediaGenerationStoreDependencies {
  agent: typeof aiMediaAgent;
  connection: IAiMediaConnection;
  pageCallbacks: IMediaGenerationPageCallbacks;
  /**
   * Hands a balance on to whoever keeps the author's credits — the balance store in production — so
   * the indicator drops the moment a generation is charged. Which balance is the newest is the
   * receiver's to decide.
   */
  acceptBalance: (balance: TQuotaBalance) => void;
  /** The wall clock, injected so counters and the overdue mark are tested by moving time. Epoch ms. */
  now: () => number;
}

/** Which slot a generation targets, as the backend names it. */
const placeholderContentIdentityOf = (content: IContent): TPlaceholderContentIdentity => ({
  courseId: content.courseID,
  coursePageId: content.coursePageID,
  pagePlaceholderId: content.pagePlaceholderID,
  contentId: content.contentID,
});

/**
 * Whether a terminal event names this content. All four ids, because none of them identifies a slot
 * on its own: a content id is unique within its placeholder and a page id within its course.
 */
const isIdentifiedBy = (content: IContent, identity: TPlaceholderContentIdentity): boolean =>
  content.courseID === identity.courseId && content.coursePageID === identity.coursePageId && content.pagePlaceholderID === identity.pagePlaceholderId && content.contentID === identity.contentId;

/** The key a failure reason is remembered under: the slot's identity, in one string. */
const failureReasonKey = (content: IContent): string => {
  const { courseId, coursePageId, pagePlaceholderId, contentId } = placeholderContentIdentityOf(content);

  return `${courseId}:${coursePageId}:${pagePlaceholderId}:${contentId}`;
};

export default class MediaGenerationStore {
  /**
   * The display clock every counter and the overdue mark read, advanced by `advanceClock`.
   * Observable so a ticking clock re-renders exactly the branches that read it — only those holding
   * a generating projection, counting or overdue; every other display state is decided without
   * looking at the time.
   */
  nowUtcMs: number;

  /**
   * What the generation dialog stands over, or null when the dialog is closed. Held by reference:
   * the page store owns the content and the placeholder inside it, and a copy of either would take
   * the author's edits and the generating state somewhere the page never looks.
   */
  dialogTarget: TGenerationDialogTarget | null = null;

  /** The working prompt as the author has it. Written onto the content on close and on generate. */
  promptDraft = '';

  /** The styles on offer, in the order the backend listed them — which is the order to offer them. */
  mediaStyles: TMediaStyle[] = [];

  selectedMediaStyleKey: string | null = null;

  isSubmitting = false;

  /**
   * A call of ours the server rejected — a refused submission, or styles that would not load. What
   * the author retries is the request, so this never speaks about the generation itself.
   */
  requestFailureMessage: string | null = null;

  /**
   * How many generations the author has on the way, against how many they may have, or null while the
   * open dialog has no reading of its own. Always the backend's count and never this page's: most of
   * what it counts runs on other pages, in other courses or in other tabs, and the hub announces only
   * endings, so nothing here could keep it.
   */
  generationAllowance: TGenerationAllowance | null = null;

  /**
   * The latest reading of the allowance asked for. An answer to an earlier one is dropped, so a slow
   * reply cannot put back a count that a later reading has already replaced.
   */
  private latestAllowanceReadingId = 0;

  /**
   * The reason a slot's last generation failed, keyed by placeholder-content identity.
   *
   * Held here rather than on the content because the projection has nowhere to put it: the backend
   * writes only a status and a time, so the words exist for as long as this page stays open and no
   * longer. A reload shows the failed state without them, which is what the projection knows.
   */
  private failureReasons = new Map<string, string>();

  private deps: IMediaGenerationStoreDependencies;

  /** Whether the owner subscription is meant to be up. Written by `attach` and `detach` only. */
  private isAttached = false;

  /**
   * Whether it actually is up. Not the same question: a hub that could not be reached leaves the
   * editor attached to a subscription it never got, and nothing else would ever ask again.
   */
  private isSubscribed = false;

  constructor(dependencies: IMediaGenerationStoreDependencies) {
    this.deps = dependencies;
    this.nowUtcMs = dependencies.now();

    makeAutoObservable<this, 'deps' | 'isAttached' | 'isSubscribed' | 'latestAllowanceReadingId'>(this, {
      deps: false,
      isAttached: false,
      isSubscribed: false,
      latestAllowanceReadingId: false,
      dialogTarget: observable.ref,
    });
  }

  advanceClock = () => {
    this.nowUtcMs = this.deps.now();
  };

  // --- The media hub. The subscription is owner-wide and lasts as long as the editor is open. ---

  /**
   * Joins the owner's media subscription, opening the socket if it is not already up.
   *
   * Owner-wide rather than per generation, so an author hears about work they started in another
   * tab — and about work they started before this page was even loaded.
   */
  attach = async (): Promise<void> => {
    if (this.isAttached) return;

    this.isAttached = true;
    await this.subscribeToMedia();
  };

  /** Leaves the subscription and closes the socket. Nothing about a running generation changes. */
  detach = async (): Promise<void> => {
    if (!this.isAttached) return;

    this.isAttached = false;
    this.isSubscribed = false;
    // The reasons went with the pages they were shown on. An editor opened again reads its slots'
    // states off the projection, which is all a reload would have had anyway.
    this.failureReasons.clear();

    await this.deps.connection.unsubscribe().catch(() => undefined);
    await this.deps.connection.stop();
  };

  /** The socket came back on its own; the group membership did not, so ask for it again. */
  resubscribe = async (): Promise<void> => {
    if (!this.isAttached) return;

    await this.subscribeToMedia();
  };

  // --- The seam: everything the socket delivers arrives here ---

  /**
   * One finished generation, applied to the page the author is looking at.
   *
   * The envelope's `success` flag is not read: a failed generation travels on a failure envelope
   * carrying its data like any other, and the outcome both of them carry is the status key on that
   * data. An event naming a slot this page has not loaded is dropped — the projection on the
   * content tells that page's author the same thing the next time they open it.
   */
  applyTerminalEnvelope = (envelope: TSignalRMediaGenerationEnvelope) => {
    // Every ending frees one of the author's places, including endings on slots this page has not
    // loaded, so the dialog's count is read again before anything here decides the event is not ours.
    void this.loadGenerationAllowance();

    const finished = envelope.data;
    if (!finished) return;

    // The credits are the author's wherever the generation ran, so the balance goes on before anything
    // decides the slot is not on this page. After a failure it already includes the refund.
    if (finished.balance) this.deps.acceptBalance(finished.balance);

    const content = this.deps.pageCallbacks.getLoadedContents().find((loaded) => isIdentifiedBy(loaded, finished));
    if (content === undefined) return;

    if (finished.mediaGenerationStatusTypeKey === 'completed') this.applyCompletion(content, finished);
    else if (finished.mediaGenerationStatusTypeKey === 'failed') this.applyFailure(content, finished);
    // An outcome the contract does not model says nothing this store can act on. Leaving the slot
    // as it stands is what keeps a future third status from being read as one of these two.
  };

  /**
   * The marker rule: a content is generateable iff its content type is Image and its AI data says
   * something — a prompt to draw from, or a status projection left by a generation that has run.
   *
   * The group's presence is deliberately not the marker, though the name suggests it: the legacy
   * page serializer materialises `aiData` on every image content, prompt and internal data alike
   * arriving null, so an ordinary image and an AI slot nobody has generated into yet are the same
   * bytes on the wire. Nothing here can tell those two apart, and marking on presence would put the
   * generate affordance on every image in the course. Only ingestion writing a prompt can separate
   * them — until it does, an untouched AI slot reads as the ordinary image it is indistinguishable
   * from. Videos carry the group too and are excluded: image generation only.
   */
  isGenerateable = (content: IContent): boolean => {
    if (content.contentType !== EContentType.Image || !content.aiData) return false;

    return Boolean(content.aiData.aiPrompt) || parseMediaGenerationStatusProjection(content.aiData.aiInternalData) !== null;
  };

  /**
   * Whether this content is in the one state a regeneration starts from: an AI-marked image showing
   * the picture a generation drew.
   *
   * Only that state, because every other one carries the affordance in the slot itself — the
   * placeholder and the failed state have the button, and a generating one deliberately has none —
   * so offering it twice would be a second door onto a dialog already one click away. Nothing is
   * asked before it runs: each generation is kept as its own media record, so the picture it
   * replaces is never overwritten.
   */
  canRegenerate = (content: IContent): boolean => this.isGenerateable(content) && this.displayStateFor(content).kind === 'image';

  /** The one question a rendering branch asks. */
  displayStateFor = (content: IContent): TMediaGenerationDisplayState => {
    if (!this.isGenerateable(content)) return { kind: 'image' };

    const projection = parseMediaGenerationStatusProjection(content.aiData?.aiInternalData);

    // A suggestion is a picture the page AI planned and nobody has drawn: the slot shows the AI
    // chrome and its generate affordance whatever the content carries, because an image standing
    // under a suggestion is placeholder art from ingestion, not a generation's answer.
    if (projection?.status === 'suggested') return { kind: 'placeholder' };

    // A generating projection without a readable time could neither count nor outrun the mark, so it
    // is read as no status at all rather than as a counter that lies.
    if (projection?.status === 'generating' && projection.statusChangedUtcMs !== null) {
      const elapsedMs = Math.max(0, this.nowUtcMs - projection.statusChangedUtcMs);

      return { kind: 'generating', secondsElapsed: Math.floor(elapsedMs / 1000), isOverdue: elapsedMs > OVERDUE_AFTER_MS };
    }

    // A failure outranks an image that is still on the content: the last word on this slot was a
    // failed generation, and the author retries from here.
    if (projection?.status === 'failed') return { kind: 'failed', reason: this.failureReasonFor(content) };

    return content.innerHTML ? { kind: 'image' } : { kind: 'placeholder' };
  };

  get isDialogOpen(): boolean {
    return this.dialogTarget !== null;
  }

  /** The content behind the dialog, or null while the media-menu flow has yet to create one. */
  get dialogContent(): IContent | null {
    return this.dialogTarget?.kind === 'content' ? this.dialogTarget.content : null;
  }

  /**
   * Whether the slot behind the dialog is generating — after a submit, or because it already was
   * when the dialog opened. The dialog shows the state rather than an action it cannot perform.
   */
  get isDialogGenerating(): boolean {
    return this.dialogDisplayState?.kind === 'generating';
  }

  /** Whether that generation has outrun what this store can account for. */
  get isDialogOverdue(): boolean {
    const displayState = this.dialogDisplayState;

    return displayState?.kind === 'generating' && displayState.isOverdue;
  }

  /**
   * Whether a generation still holds the dialog shut. An overdue one does not: a wait nobody can
   * account for is exactly when the author has to be able to ask again, so the prompt, the styles
   * and the action all come back to them.
   */
  get isDialogLockedByGeneration(): boolean {
    return this.isDialogGenerating && !this.isDialogOverdue;
  }

  /** How long the slot behind the dialog has been generating, for the readout to show. */
  get dialogSecondsElapsed(): number {
    const displayState = this.dialogDisplayState;

    return displayState?.kind === 'generating' ? displayState.secondsElapsed : 0;
  }

  private get dialogDisplayState(): TMediaGenerationDisplayState | null {
    const content = this.dialogContent;

    return content === null ? null : this.displayStateFor(content);
  }

  get selectedMediaStyle(): TMediaStyle | undefined {
    return this.mediaStyles.find((style) => style.mediaStyleKey === this.selectedMediaStyleKey);
  }

  /**
   * What is wrong with the prompt, for the author to read. An empty prompt is the state the dialog
   * usually opens in, so it is not shouted about — it only blocks the action.
   */
  get promptValidationMessage(): string | null {
    return this.promptDraft.length > MAX_PROMPT_LENGTH ? `Keep the prompt to ${MAX_PROMPT_LENGTH} characters or fewer.` : null;
  }

  /** An allowance nobody could read holds nothing back: the backend's 429 is the rule either way. */
  get isAtGenerationLimit(): boolean {
    const allowance = this.generationAllowance;

    return allowance !== null && allowance.unfinishedCount >= allowance.limit;
  }

  /**
   * What the dialog says about the allowance, beside Generate. The count says what it counts, because
   * a bare "2 of 3" could as well be the places left as the places taken.
   */
  get generationAllowanceMessage(): string | null {
    const allowance = this.generationAllowance;
    if (allowance === null) return null;

    const { unfinishedCount, limit } = allowance;
    if (unfinishedCount === 0) return `You can generate up to ${limit} images at once.`;

    const generating = `${unfinishedCount} of ${limit} images generating`;

    return this.isAtGenerationLimit ? `${generating}. Wait until one finishes to start another.` : generating;
  }

  /**
   * At the limit Generate is held back rather than left to the backend to refuse, because the media
   * menu creates its content before it asks: a refused submission there would leave an empty image
   * content on the page.
   */
  get canSubmit(): boolean {
    if (this.dialogTarget === null || this.isSubmitting || this.isDialogLockedByGeneration || this.isAtGenerationLimit) return false;

    return this.promptDraft.trim().length > 0 && this.promptValidationMessage === null && this.selectedMediaStyle !== undefined;
  }

  /**
   * Opens the dialog over an AI-marked content. The prompt is prefilled from the value the page load
   * already carried, so the dialog is readable at once.
   */
  openDialogFor = async (content: IContent): Promise<void> => {
    this.resetDialog({ kind: 'content', content });

    await Promise.all([this.loadMediaStyles(content.courseID), this.loadGenerationAllowance()]);
  };

  /**
   * Opens the dialog over a placeholder with nothing in it, reached from the media menu.
   *
   * Nothing is created here and no property is fetched: there is no content to fetch one from, and
   * making one now would mean an author who changed their mind had left an empty image on the page.
   */
  openDialogForPlaceholder = async (placeholder: IPagePlaceholder): Promise<void> => {
    this.resetDialog({ kind: 'emptyPlaceholder', placeholder });

    await Promise.all([this.loadMediaStyles(placeholder.courseID), this.loadGenerationAllowance()]);
  };

  /**
   * The state every dialog opens in. What it opens *on* is the target's to say: a planned slot opens
   * on the wording the page load already carried, and a media-menu one on nothing at all.
   */
  private resetDialog = (target: TGenerationDialogTarget) => {
    this.dialogTarget = target;
    this.promptDraft = target.kind === 'content' ? (target.content.aiData?.aiPrompt ?? '') : '';
    this.requestFailureMessage = null;
    this.isSubmitting = false;
    this.generationAllowance = null;
    this.selectMediaStyleFor(courseIdOf(target));
  };

  setPromptDraft = (prompt: string) => {
    this.promptDraft = prompt;
  };

  setSelectedMediaStyleKey = (mediaStyleKey: string) => {
    this.selectedMediaStyleKey = mediaStyleKey;
  };

  /** Closes the dialog, saving the author's wording on the way out. No separate save button exists. */
  closeDialog = async (): Promise<void> => {
    const content = this.dialogContent;
    const prompt = this.promptDraft;

    this.clearDialog();

    // A dialog with nothing behind it has nowhere to save the wording, which is the point: an author
    // who browsed the media menu's option and closed it again leaves the page as they found it.
    if (content !== null) await this.persistPrompt(content, prompt);
  };

  /**
   * Submits the generation. An accepted one closes the dialog and leaves the state on the content,
   * so the author can carry on editing the page; a refusal keeps the dialog standing with the
   * prompt intact and the backend's own words on screen.
   */
  submit = async (): Promise<void> => {
    const target = this.dialogTarget;
    const style = this.selectedMediaStyle;
    if (target === null || style === undefined || !this.canSubmit) return;

    const prompt = this.promptDraft;
    this.isSubmitting = true;
    this.requestFailureMessage = null;

    // A subscription that never came up — a hub that was down when the editor opened, a socket
    // whose own retries ran out — is worth one more attempt at the one moment a terminal event
    // starts mattering to this author. Not waited on: a generation takes minutes and a handshake
    // takes moments, and the submission does not depend on the outcome either way.
    if (this.isAttached && !this.isSubscribed) void this.subscribeToMedia();

    try {
      const content = await this.contentToGenerateInto(target, prompt);
      if (content === null) {
        runInAction(() => {
          this.requestFailureMessage = CONTENT_NOT_CREATED_MESSAGE;
        });
        return;
      }

      const outcome = await this.deps.agent.AiMedia.generateImage({ ...placeholderContentIdentityOf(content), mediaStyleId: style.mediaStyleId, prompt });

      if (outcome.kind === 'notEnoughCredits') {
        // The refusal says how the credits stand, so the indicator cannot go on showing enough.
        if (outcome.balance !== null) this.deps.acceptBalance(outcome.balance);
        runInAction(() => this.markRefusedForCredits(content, outcome.reason));
        return;
      }

      rememberMediaStyleKey(content.courseID, style.mediaStyleKey);
      if (outcome.kind === 'accepted' && outcome.balance !== null) this.deps.acceptBalance(outcome.balance);
      runInAction(() => {
        this.markGenerating(content, outcome);
        if (outcome.kind === 'accepted') this.clearDialog();
      });
    } catch (error) {
      runInAction(() => {
        this.requestFailureMessage = resolveErrorMessage(error, GENERATION_REFUSED_MESSAGE);
      });
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });

      // An accepted submission has closed the dialog, so this reads only after one that started
      // nothing, whose reason may be a limit reached in another tab. Which refusal it was is the
      // agent's business; the count is read again either way.
      void this.loadGenerationAllowance();
    }
  };

  /**
   * The author put the picture in the slot themselves, chosen from the media library rather than
   * drawn. The AI chrome comes off it and stays off: the projection it is read from is written on
   * the content and saved, so a reload does not bring the suggestion back over the author's choice.
   *
   * `completed` rather than a cleared projection, though no generation ran: the status says the slot
   * has the picture it was waiting for, which is the whole of what the rendering branch asks it, and
   * it is the state a regeneration starts from — an author who tires of their own choice still has
   * Generate Image in the content menu. The image itself is the page's to put on the content; this
   * only ends the slot's AI lifecycle around it.
   */
  applyManualSelection = async (content: IContent): Promise<void> => {
    if (!content.aiData) return;

    // Whatever the last generation had to say about this slot is no longer the last word on it.
    this.clearFailureReason(content);
    this.writeStatusProjection(content, 'completed');

    await this.persistStatusProjection(content);
  };

  /**
   * The write itself. A save that fails costs the projection and not the picture: the image is
   * already on the content, and the slot renders as the image it now holds until the next reload
   * reads the backend's own copy back.
   */
  private persistStatusProjection = async (content: IContent): Promise<void> => {
    const statusProjection = content.aiData?.aiInternalData;
    if (!statusProjection) return;

    try {
      await this.deps.pageCallbacks.saveStatusProjection(content, statusProjection);
    } catch (error) {
      console.warn('Could not save the media generation status projection', error);
    }
  };

  /**
   * The content this submission generates into, carrying the author's wording, or null when the page
   * could not produce one.
   *
   * A planned slot already has its content and only needs the wording saved. A media-menu one is
   * created here and nowhere else — create-on-Generate — and the dialog's target becomes it, so a
   * refusal the author retries generates into the content already on the page instead of adding a
   * second one beside it.
   */
  private contentToGenerateInto = async (target: TGenerationDialogTarget, prompt: string): Promise<IContent | null> => {
    if (target.kind === 'content') {
      await this.persistPrompt(target.content, prompt);
      return target.content;
    }

    const content = await this.createMarkedImageContent(target.placeholder, prompt);
    if (content === null) return null;

    runInAction(() => {
      if (this.dialogTarget === target) this.dialogTarget = { kind: 'content', content };
    });

    return content;
  };

  /**
   * Creates an empty image content in the placeholder and marks it as the AI's.
   *
   * The marker is the AI prompt property: writing it is the whole of what makes this an AI image,
   * one that renders the generation lifecycle states and can be regenerated later. The local copy
   * goes on first and a write that fails is not turned back, because turning back is worse: the
   * content is already on the page, and abandoning it there leaves the empty image slot this whole
   * feature exists to do away with. Generating on costs only the marker after a reload — the image
   * itself lands either way, since a terminal event finds its slot by identity and not by marker.
   */
  private createMarkedImageContent = async (placeholder: IPagePlaceholder, prompt: string): Promise<IContent | null> => {
    const content = await this.createImageContent(placeholder);
    if (content === null) return null;

    runInAction(() => {
      content.aiData = { aiPrompt: prompt, aiInternalData: '', description: '' };
    });

    await this.savePrompt(content, prompt);

    return content;
  };

  /** The page making the content, with a failure to create one reported as one rather than thrown. */
  private createImageContent = async (placeholder: IPagePlaceholder): Promise<IContent | null> => {
    try {
      return await this.deps.pageCallbacks.createImageContent(placeholder);
    } catch (error) {
      console.warn('Could not create the image content for a media generation', error);
      return null;
    }
  };

  /**
   * Opens the socket and joins the group.
   *
   * A hub that cannot be reached costs the live update and nothing else, so it is logged rather
   * than put in front of the author: the editor opens either way, and the status projection on the
   * content still reports every outcome the next time the page is loaded.
   */
  private subscribeToMedia = async (): Promise<void> => {
    try {
      await this.deps.connection.start();

      // A detach that landed while the handshake was in flight has already closed the socket;
      // joining a group on it now would be joining one nobody is listening on.
      if (!this.isAttached) return;

      await this.deps.connection.subscribe();
      this.isSubscribed = true;
    } catch (error) {
      this.isSubscribed = false;
      console.warn('Could not subscribe to the media hub. Generation results will appear on the next page load.', error);
    }
  };

  /**
   * The image is already on the content in the database — the backend wrote both it and the alt
   * text before it announced anything. So this only catches the page up with what is already true,
   * and saves nothing: a save here would be the author's editor writing back what it was just told.
   */
  private applyCompletion = (content: IContent, finished: TSignalRMediaGenerationFinishedData) => {
    this.clearFailureReason(content);

    if (finished.mediaUrl) content.innerHTML = finished.mediaUrl;
    // Alt text the generation could not produce leaves whatever the content had; the author's own
    // wording is not worth losing to a description the backend never wrote.
    if (content.image !== undefined && finished.altText) content.image.altText = finished.altText;

    this.writeStatusProjection(content, 'completed');
  };

  private applyFailure = (content: IContent, finished: TSignalRMediaGenerationFinishedData) => {
    this.clearFailureReason(content);
    this.writeStatusProjection(content, 'failed');

    // The reason belongs to the failed projection just written. A content with no AI data to write
    // one on has no failed state to show it in either, so keeping the words would be keeping them
    // for nobody — and nothing would ever clear them.
    if (finished.failureReason && content.aiData) this.failureReasons.set(failureReasonKey(content), finished.failureReason);
  };

  private failureReasonFor = (content: IContent): string | null => this.failureReasons.get(failureReasonKey(content)) ?? null;

  private clearFailureReason = (content: IContent) => {
    this.failureReasons.delete(failureReasonKey(content));
  };

  private clearDialog = () => {
    this.dialogTarget = null;
    this.promptDraft = '';
    this.requestFailureMessage = null;
    this.isSubmitting = false;
    this.generationAllowance = null;
  };

  /**
   * Writes the generating projection onto the loaded content so the slot shows the state at once,
   * rather than waiting for a reload to read the backend's own copy.
   */
  private markGenerating = (content: IContent, outcome: TGenerateImageOutcome) => {
    // Whatever went wrong last time is no longer the last word on this slot: a generation is
    // running again, and the reason must not be waiting behind it for the counter to run out.
    this.clearFailureReason(content);

    // An accepted generation starts now, so the counter starts now. A conflict means one was already
    // running: a generating projection already on the content keeps counting from its own start
    // rather than restarting at zero.
    if (outcome.kind === 'alreadyGenerating' && this.displayStateFor(content).kind === 'generating') return;

    this.writeStatusProjection(content, 'generating');
  };

  /**
   * The backend refused for want of credits, or for a debt on purchased credits beyond the limit,
   * after it had already recorded the attempt on the slot: it finished it as failed and wrote that onto
   * the content. The loaded copy says the same, so the page shows now what a reload would, and the
   * dialog stays standing with the reason on screen, worded for why.
   */
  private markRefusedForCredits = (content: IContent, reason: TCreditsRefusalReason) => {
    const message = reason === 'inDebt' ? IN_DEBT_FOR_AN_IMAGE_MESSAGE : NOT_ENOUGH_CREDITS_FOR_AN_IMAGE_MESSAGE;
    this.requestFailureMessage = message;
    this.writeStatusProjection(content, 'failed');
    if (content.aiData) this.failureReasons.set(failureReasonKey(content), message);
  };

  /**
   * Puts a status on the loaded content the way the backend's own projection write would, so the
   * slot renders the new state without waiting for a reload to fetch it. The page is not marked
   * unsaved by it: the property this stands in for is the backend's to write, not the editor's.
   */
  private writeStatusProjection = (content: IContent, status: TMediaGenerationStatus) => {
    if (!content.aiData) return;

    content.aiData.aiInternalData = JSON.stringify({ status, statusChangedUtc: new Date(this.deps.now()).toISOString() });
  };

  /**
   * Saves the working prompt onto the content's AI prompt property. A prompt the author did not
   * change costs no round trip, and a save that fails never blocks generating — the prompt travels
   * with the request regardless.
   */
  private persistPrompt = async (content: IContent, prompt: string): Promise<void> => {
    if (prompt === (content.aiData?.aiPrompt ?? '')) return;

    await this.savePrompt(content, prompt);
  };

  /** The write itself, handed to the page's property machinery; the local copy follows so reopening shows the edit. */
  private savePrompt = async (content: IContent, prompt: string): Promise<void> => {
    try {
      await this.deps.pageCallbacks.savePrompt(content, prompt);
      runInAction(() => {
        if (content.aiData) content.aiData.aiPrompt = prompt;
      });
    } catch (error) {
      console.warn('Could not save the media generation prompt', error);
    }
  };

  private loadMediaStyles = async (courseId: number): Promise<void> => {
    try {
      const styles = await this.deps.agent.AiMedia.getMediaStyles();
      runInAction(() => {
        this.mediaStyles = styles;
        // A choice the author has already made stands, unless the list that just arrived no longer
        // offers it — an administrator can retire a style between one dialog and the next.
        if (this.selectedMediaStyle === undefined) this.selectMediaStyleFor(courseId);
      });
    } catch (error) {
      runInAction(() => {
        this.requestFailureMessage = resolveErrorMessage(error, GENERATION_REFUSED_MESSAGE);
      });
    }
  };

  /**
   * A reading that fails shows no count rather than an old one, and is not put in front of the
   * author: the dialog works without it.
   */
  private loadGenerationAllowance = async (): Promise<void> => {
    if (!this.isDialogOpen) return;

    this.latestAllowanceReadingId += 1;
    const reading = this.latestAllowanceReadingId;

    let allowance: TGenerationAllowance | null = null;
    try {
      allowance = await this.deps.agent.AiMedia.getGenerationAllowance();
    } catch (error) {
      console.warn('Could not read how many images the author has generating', error);
    }

    runInAction(() => {
      if (reading === this.latestAllowanceReadingId && this.isDialogOpen) this.generationAllowance = allowance;
    });
  };

  /** Style memory, then the first style on offer — which the backend has already sorted and filtered. */
  private selectMediaStyleFor = (courseId: number) => {
    const remembered = readRememberedMediaStyleKey(courseId);
    if (remembered !== null && this.mediaStyles.some((style) => style.mediaStyleKey === remembered)) {
      this.selectedMediaStyleKey = remembered;
      return;
    }

    this.selectedMediaStyleKey = this.mediaStyles.length > 0 ? this.mediaStyles[0].mediaStyleKey : null;
  };
}
